import React, { useState, useEffect } from 'react';
import { auditorsApi } from '../api';
import { toast } from 'sonner';
import AuditorReport from './AuditorReport';
import {
  UserCheck,
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  Loader2,
  CheckCircle,
  XCircle,
  Mail,
  GraduationCap,
  Paperclip,
  FileText,
  Download,
  Users,
  BarChart3,
} from 'lucide-react';

const Auditors = () => {
  const [view, setView] = useState('cadastro');
  const [auditors, setAuditors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAuditor, setEditingAuditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    email: '',
    qualification: '',
    is_active: true,
  });

  useEffect(() => {
    loadAuditors();
  }, []);

  const loadAuditors = async () => {
    try {
      setLoading(true);
      const response = await auditorsApi.list();
      setAuditors(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar auditores');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = async (auditor = null) => {
    if (auditor) {
      try {
        const res = await auditorsApi.get(auditor.id);
        const fullAuditor = res.data;
        setEditingAuditor(fullAuditor);
        setAttachments(fullAuditor.attachments || []);
        setFormData({
          code: fullAuditor.code || '',
          name: fullAuditor.name || '',
          email: fullAuditor.email || '',
          qualification: fullAuditor.qualification || '',
          is_active: fullAuditor.is_active !== false,
        });
        setShowModal(true);
      } catch (error) {
        toast.error('Erro ao carregar dados do auditor');
      }
    } else {
      setEditingAuditor(null);
      setAttachments([]);
      setFormData({
        code: '',
        name: '',
        email: '',
        qualification: '',
        is_active: true,
      });
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAuditor(null);
    setAttachments([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name) {
      toast.error('O nome do auditor é obrigatório');
      return;
    }

    setSaving(true);

    const cleanedData = {
      ...formData,
      code: formData.code || null,
      email: formData.email || null,
      qualification: formData.qualification || null,
    };

    try {
      if (editingAuditor) {
        await auditorsApi.update(editingAuditor.id, cleanedData);
        toast.success('Auditor atualizado com sucesso!');
      } else {
        await auditorsApi.create(cleanedData);
        toast.success('Auditor cadastrado com sucesso!');
      }

      handleCloseModal();
      loadAuditors();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = Array.isArray(detail)
        ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ')
        : (detail || 'Erro ao salvar auditor');
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (auditor) => {
    if (!window.confirm(`Desativar o auditor "${auditor.name}"?`)) return;

    try {
      await auditorsApi.delete(auditor.id);
      toast.success('Auditor desativado com sucesso!');
      loadAuditors();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Erro ao desativar auditor');
    }
  };

  const handleUploadAttachment = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const auditorId = editingAuditor?.id;
    if (!auditorId) {
      toast.error('Salve o auditor antes de anexar documentos.');
      e.target.value = '';
      return;
    }

    setUploading(true);
    try {
      for (const file of files) {
        const res = await auditorsApi.uploadAttachment(auditorId, file);
        setAttachments((prev) => [...prev, res.data]);
      }
      toast.success('Documento(s) anexado(s)!');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Erro ao anexar documento');
    } finally {
      setUploading(false);
    }
    e.target.value = '';
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const res = await auditorsApi.downloadAttachment(attachment.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar documento');
    }
  };

  const handleDeleteAttachment = async (attachment) => {
    if (!window.confirm(`Remover o documento "${attachment.file_name}"?`)) return;

    try {
      await auditorsApi.deleteAttachment(attachment.id);
      setAttachments((prev) => prev.filter((a) => a.id !== attachment.id));
      toast.success('Documento removido');
    } catch (error) {
      toast.error('Erro ao remover documento');
    }
  };

  const filteredAuditors = auditors.filter(auditor =>
    auditor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    auditor.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    auditor.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="auditors-page">
      {/* Sub-navigation */}
      <div className="flex gap-1 border-b border-gray-700">
        {[
          { id: 'cadastro', label: 'Cadastro', icon: UserCheck },
          { id: 'relatorio', label: 'Relatório de Avaliações', icon: BarChart3 },
        ].map((sub) => (
          <button
            key={sub.id}
            onClick={() => setView(sub.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
              view === sub.id ? 'border-green-500 text-green-400' : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            <sub.icon size={16} />
            {sub.label}
          </button>
        ))}
      </div>

      {view === 'relatorio' ? (
        <AuditorReport />
      ) : (
      <>
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <UserCheck className="text-green-400" />
            Cadastro de Auditores
          </h1>
          <p className="text-gray-400 mt-1">Cadastro de auditores utilizados nas auditorias internas e externas</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="btn-primary"
          data-testid="add-auditor-btn"
        >
          <Plus size={18} />
          Novo Auditor
        </button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
          <input
            type="text"
            placeholder="Buscar por nome, código ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input w-full pl-10"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <UserCheck className="text-blue-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Total de Auditores</p>
              <p className="text-2xl font-bold text-blue-400">{auditors.length}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <CheckCircle className="text-green-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Ativos</p>
              <p className="text-2xl font-bold text-green-400">
                {auditors.filter(a => a.is_active).length}
              </p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Users className="text-purple-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Inativos</p>
              <p className="text-2xl font-bold text-purple-400">
                {auditors.filter(a => !a.is_active).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Código</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Nome</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Qualificação</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredAuditors.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    Nenhum auditor encontrado
                  </td>
                </tr>
              ) : (
                filteredAuditors.map(auditor => (
                  <tr key={auditor.id} className="hover:bg-gray-800/50">
                    <td className="px-4 py-3 font-mono text-white">{auditor.code}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-white">{auditor.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-400">{auditor.email || '-'}</td>
                    <td className="px-4 py-3 text-gray-400 max-w-xs truncate">
                      {auditor.qualification || '-'}
                    </td>
                    <td className="px-4 py-3">
                      {auditor.is_active ? (
                        <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400 flex items-center gap-1 w-fit">
                          <CheckCircle size={12} />
                          Ativo
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400 flex items-center gap-1 w-fit">
                          <XCircle size={12} />
                          Inativo
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleOpenModal(auditor)}
                          className="p-2 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded-lg"
                          title="Editar"
                        >
                          <Edit size={18} />
                        </button>
                        {auditor.is_active && (
                          <button
                            onClick={() => handleDelete(auditor)}
                            className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg"
                            title="Desativar"
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Auditor Form Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h2 className="text-lg font-semibold text-white">
                {editingAuditor ? 'Editar Auditor' : 'Novo Auditor'}
              </h2>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Código</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="AUD-001 (auto)"
                    className="input w-full"
                    data-testid="auditor-code-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nome do Auditor <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: João da Silva"
                    className="input w-full"
                    required
                    data-testid="auditor-name-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <Mail size={14} className="inline mr-1" />
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@empresa.com"
                  className="input w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <GraduationCap size={14} className="inline mr-1" />
                  Qualificação / Formação
                </label>
                <textarea
                  value={formData.qualification}
                  onChange={(e) => setFormData({ ...formData, qualification: e.target.value })}
                  placeholder="Cursos, certificações de auditor (ex: ISO 9001:2015), etc."
                  className="input w-full"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <Paperclip size={14} className="inline mr-1" />
                  Documentação / Anexos
                </label>
                {attachments.length === 0 ? (
                  <p className="text-sm text-gray-500 mb-3">Nenhum documento anexado.</p>
                ) : (
                  <ul className="space-y-2 mb-3">
                    {attachments.map((att) => (
                      <li
                        key={att.id}
                        className="flex items-center justify-between gap-2 border border-gray-700 rounded-lg px-3 py-2 bg-gray-800/40"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText size={16} className="text-green-500 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm text-gray-200 truncate">{att.file_name}</p>
                            <p className="text-xs text-gray-500">
                              {att.file_size ? `${(att.file_size / 1024).toFixed(1)} KB` : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => handleDownloadAttachment(att)}
                            className="p-1.5 text-blue-400 hover:text-blue-300"
                            title="Baixar"
                          >
                            <Download size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteAttachment(att)}
                            className="p-1.5 text-red-400 hover:text-red-300"
                            title="Remover"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {editingAuditor ? (
                  <label
                    className={`flex items-center justify-center gap-2 border border-dashed border-gray-600 rounded-lg px-3 py-3 cursor-pointer hover:border-green-500 transition-colors ${
                      uploading ? 'opacity-60 pointer-events-none' : ''
                    }`}
                  >
                    {uploading ? <Loader2 size={18} className="animate-spin" /> : <Paperclip size={18} />}
                    <span className="text-sm text-gray-300">{uploading ? 'Enviando...' : 'Anexar documento'}</span>
                    <input type="file" className="hidden" multiple onChange={handleUploadAttachment} disabled={uploading} />
                  </label>
                ) : (
                  <p className="text-xs text-gray-500">Salve o auditor para anexar a documentação.</p>
                )}
              </div>

              <div className="flex items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-green-500"
                  />
                  <span className="text-sm font-medium text-gray-300">Auditor Ativo</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button type="button" onClick={handleCloseModal} className="btn-secondary" disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
                  {saving ? <><Loader2 size={18} className="animate-spin" /> Salvando...</> : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default Auditors;
